/**
 * Willtec Product Form Custom Element
 * Handles AJAX product additions via Shopify Product Form & Ajax Cart API (cart/add.js).
 * Supports quantity selector increment/decrement, loading states, error handling,
 * bundled section rendering for cart drawer and header cart count, and opening the cart drawer.
 */
(() => {
  if (customElements.get('willtec-product-form')) return;

  class WilltecProductForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.submitBtn = this.form.querySelector('[type="submit"]');
      this.qtyInput = this.form.querySelector('input[name="quantity"]');
      this.qtyDisplay = this.querySelector('[data-card-qty-val]');
      this.minusBtn = this.querySelector('[data-card-qty-minus]');
      this.plusBtn = this.querySelector('[data-card-qty-plus]');
      this.errorContainer = this.querySelector('[data-card-error]');

      this.bindQuantityControls();
      this.bindSubmit();
    }

    getQuantity() {
      const el = (this.qtyDisplay && this.qtyDisplay.tagName === 'INPUT') ? this.qtyDisplay : (this.qtyInput || this.qtyDisplay);
      if (!el) return 1;
      const raw = el.tagName === 'INPUT' ? el.value : el.textContent;
      const val = parseInt(raw, 10);
      return isNaN(val) || val < 1 ? 1 : val;
    }

    setQuantity(num, format = true) {
      const safeVal = Math.max(1, parseInt(num, 10) || 1);
      const displayVal = format && safeVal < 10 ? `0${safeVal}` : `${safeVal}`;

      if (this.qtyInput) {
        this.qtyInput.value = safeVal;
      }

      if (this.qtyDisplay) {
        if (this.qtyDisplay.tagName === 'INPUT') {
          this.qtyDisplay.value = format ? displayVal : safeVal;
        } else {
          this.qtyDisplay.textContent = displayVal;
        }
      }

      return safeVal;
    }

    bindQuantityControls() {
      if (!this.qtyInput && !this.qtyDisplay) return;

      if (this.minusBtn) {
        this.minusBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const current = this.getQuantity();
          if (current > 1) {
            this.setQuantity(current - 1);
          }
        });
      }

      if (this.plusBtn) {
        this.plusBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const current = this.getQuantity();
          this.setQuantity(current + 1);
        });
      }

      const inputEl = (this.qtyDisplay && this.qtyDisplay.tagName === 'INPUT')
        ? this.qtyDisplay
        : (this.qtyInput && this.qtyInput.type !== 'hidden' ? this.qtyInput : null);

      if (inputEl) {
        // Automatically select text on focus for quick typing
        inputEl.addEventListener('focus', () => {
          inputEl.select();
        });

        // Sync without altering cursor during input
        inputEl.addEventListener('input', () => {
          const val = parseInt(inputEl.value, 10);
          if (!isNaN(val) && val >= 1) {
            if (this.qtyInput && this.qtyInput !== inputEl) {
              this.qtyInput.value = val;
            }
          }
        });

        const commitValue = () => {
          const current = parseInt(inputEl.value, 10);
          if (isNaN(current) || current < 1) {
            this.setQuantity(1);
          } else {
            this.setQuantity(current);
          }
        };

        inputEl.addEventListener('blur', commitValue);
        inputEl.addEventListener('change', commitValue);

        inputEl.addEventListener('keydown', (e) => {
          // Block non-numeric characters e, +, -, .
          if (['e', 'E', '+', '-', '.'].includes(e.key)) {
            e.preventDefault();
            return;
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            commitValue();
            if (this.submitBtn && !this.submitBtn.disabled) {
              this.submitBtn.click();
            }
          }
        });
      }
    }

    bindSubmit() {
      this.form.addEventListener('submit', async (e) => {
        e.preventDefault();

        // Ensure valid quantity is committed before creating FormData
        const currentQty = this.getQuantity();
        this.setQuantity(currentQty);

        if (this.errorContainer) {
          this.errorContainer.classList.add('hidden');
          this.errorContainer.textContent = '';
        }

        if (this.submitBtn) {
          this.submitBtn.disabled = true;
          this.submitBtn.setAttribute('aria-busy', 'true');
          this.originalBtnText = this.submitBtn.innerHTML;
          this.submitBtn.innerHTML = '<span class="animate-pulse">Adicionando...</span>';
        }

        const formData = new FormData(this.form);
        formData.append('sections', 'willtec-cart-drawer,header');
        formData.append('sections_url', window.location.pathname);

        const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';

        try {
          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: {
              'Accept': 'application/json'
            },
            body: formData
          });

          const data = await response.json();

          if (response.ok) {
            // Bundled section rendering update if returned
            if (data.sections) {
              const drawerEl = document.getElementById('WilltecCartDrawer');
              if (drawerEl && data.sections['willtec-cart-drawer']) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(data.sections['willtec-cart-drawer'], 'text/html');
                const newDrawerContent = doc.getElementById('WilltecCartDrawer');
                if (newDrawerContent) {
                  drawerEl.innerHTML = newDrawerContent.innerHTML;
                  if (typeof drawerEl.bindEvents === 'function') {
                    drawerEl.bindEvents();
                  }
                }
              }

              if (data.sections['header']) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(data.sections['header'], 'text/html');
                const newHeaderCounts = doc.querySelectorAll('.willtec-cart-count');
                document.querySelectorAll('.willtec-cart-count').forEach((badge, idx) => {
                  if (newHeaderCounts[idx]) {
                    badge.innerHTML = newHeaderCounts[idx].innerHTML;
                  }
                });
              }
            }

            // Trigger global cart events
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            document.dispatchEvent(new CustomEvent('cart:open'));
            document.dispatchEvent(new CustomEvent('willtec:cart:open'));

            // Reset quantity to 1
            this.setQuantity(1);
          } else {
            // Render error feedback (e.g. 422 stock error)
            const errorMsg = data.description || data.message || 'Não foi possível adicionar o produto ao carrinho.';
            if (this.errorContainer) {
              this.errorContainer.textContent = errorMsg;
              this.errorContainer.classList.remove('hidden');
            }
          }
        } catch (err) {
          console.error('Willtec Product Form Error:', err);
          if (this.errorContainer) {
            this.errorContainer.textContent = 'Erro ao se comunicar com o servidor. Tente novamente.';
            this.errorContainer.classList.remove('hidden');
          }
        } finally {
          if (this.submitBtn) {
            this.submitBtn.disabled = false;
            this.submitBtn.removeAttribute('aria-busy');
            this.submitBtn.innerHTML = this.originalBtnText;
          }
        }
      });
    }
  }

  if (!customElements.get('willtec-product-form')) {
    customElements.define('willtec-product-form', WilltecProductForm);
  }
})();
